import { createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  acceptProposedTimeThunk,
  cancelCalendarEventThunk,
  createCalendarEventThunk,
  fetchAllProjectsDocuments,
  fetchAllProjectsInvoices,
  fetchAllProjectsPhotos,
  fetchPortalAlbums,
  fetchPortalCalendarEvents,
  fetchPortalDocuments,
  fetchPortalInvoices,
  fetchPortalPhotos,
  fetchPortalProfile,
  fetchPortalProjectDetail,
  fetchPortalProjects,
  requestCalendarRescheduleThunk,
  rsvpToCalendarEventThunk,
  updateCalendarEventThunk,
  updateProfileThunk,
  updateReminderPreferenceThunk,
  uploadDocumentThunk,
  uploadPhotoThunk
} from "./portal.thunks.ts"
import { type PortalState } from "../types/portal.types.ts"

const initialState: PortalState = {
  projects: [],
  projectsFetching: false,
  projectsLoaded: false,
  projectsError: false,

  selectedProjectId: null,

  projectDetail: null,
  projectDetailFetching: false,
  projectDetailError: false,

  invoices: [],
  invoicesFetching: false,
  invoicesError: false,

  documents: [],
  documentsFetching: false,
  documentsLoaded: false,
  documentsError: false,

  albums: [],
  albumsFetching: false,
  albumsLoaded: false,
  albumsError: false,

  photos: [],
  photosFetching: false,
  photosLoaded: false,
  photosError: false,

  crossProjectInvoices: [],
  crossProjectInvoicesFetching: false,
  crossProjectInvoicesLoaded: false,
  crossProjectDocuments: [],
  crossProjectDocumentsFetching: false,
  crossProjectDocumentsLoaded: false,
  crossProjectPhotos: [],
  crossProjectPhotosFetching: false,
  crossProjectPhotosLoaded: false,

  profile: null,
  profileFetching: false,
  profileSaving: false,
  profileError: false,
  profileConflict: false,

  calendarEvents: [],
  calendarEventsFetching: false,
  calendarEventsLoaded: false,
  calendarEventsError: false
}

const clearSingleProjectData = (state: PortalState) => {
  state.projectDetail = null
  state.invoices = []
  state.documents = []
  state.documentsLoaded = false
  state.albums = []
  state.albumsLoaded = false
  state.photos = []
  state.photosLoaded = false
  state.calendarEvents = []
  state.calendarEventsLoaded = false
}

const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    // `null` is "All projects" — a deliberate context, not just "nothing
    // loaded yet". Switching (either direction) must not leave stale
    // single-project or cross-project data visible while new requests are
    // in flight.
    selectPortalProject: (state, action: { payload: string | null }) => {
      if (state.selectedProjectId === action.payload) return
      state.selectedProjectId = action.payload
      clearSingleProjectData(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortalProjects.pending, (state) => {
        state.projectsFetching = true
        state.projectsError = false
      })
      .addCase(fetchPortalProjects.fulfilled, (state, action) => {
        state.projectsFetching = false
        state.projectsLoaded = true
        state.projects = action.payload

        const stillAccessible = action.payload.some(
          (p) => p.id === state.selectedProjectId
        )
        if (!stillAccessible) {
          // Exactly one accessible project opens automatically — a
          // customer with several defaults to "All projects" (`null`), and
          // a selection that is no longer in the list (access removed)
          // falls back the same way. Never override a still-valid
          // selection made via a deep link or the project selector.
          state.selectedProjectId = action.payload.length === 1 ? action.payload[0].id : null
        }
      })
      .addCase(fetchPortalProjects.rejected, (state) => {
        state.projectsFetching = false
        state.projectsLoaded = true
        state.projectsError = true
      })

      .addCase(fetchPortalProjectDetail.pending, (state) => {
        state.projectDetailFetching = true
        state.projectDetailError = false
      })
      .addCase(fetchPortalProjectDetail.fulfilled, (state, action) => {
        state.projectDetailFetching = false
        state.projectDetail = action.payload
      })
      .addCase(fetchPortalProjectDetail.rejected, (state) => {
        state.projectDetailFetching = false
        state.projectDetailError = true
        state.projectDetail = null
      })

      .addCase(fetchPortalInvoices.pending, (state) => {
        state.invoicesFetching = true
        state.invoicesError = false
      })
      .addCase(fetchPortalInvoices.fulfilled, (state, action) => {
        state.invoicesFetching = false
        state.invoices = action.payload
      })
      .addCase(fetchPortalInvoices.rejected, (state) => {
        state.invoicesFetching = false
        state.invoicesError = true
        state.invoices = []
      })

      .addCase(fetchPortalDocuments.pending, (state) => {
        state.documentsFetching = true
        state.documentsError = false
      })
      .addCase(fetchPortalDocuments.fulfilled, (state, action) => {
        state.documentsFetching = false
        state.documentsLoaded = true
        state.documents = action.payload
      })
      .addCase(fetchPortalDocuments.rejected, (state) => {
        state.documentsFetching = false
        state.documentsLoaded = true
        state.documentsError = true
        state.documents = []
      })
      .addCase(uploadDocumentThunk.fulfilled, (state, action) => {
        // The list is the single source of truth Home also reads — insert
        // rather than waiting for a refetch, so "Shared by you" and Recent
        // documents both pick it up immediately.
        state.documents = [action.payload, ...state.documents]
      })

      .addCase(fetchPortalAlbums.pending, (state) => {
        state.albumsFetching = true
        state.albumsError = false
      })
      .addCase(fetchPortalAlbums.fulfilled, (state, action) => {
        state.albumsFetching = false
        state.albumsLoaded = true
        state.albums = action.payload
      })
      .addCase(fetchPortalAlbums.rejected, (state) => {
        state.albumsFetching = false
        state.albumsLoaded = true
        state.albumsError = true
        state.albums = []
      })

      .addCase(fetchPortalPhotos.pending, (state) => {
        state.photosFetching = true
        state.photosError = false
      })
      .addCase(fetchPortalPhotos.fulfilled, (state, action) => {
        state.photosFetching = false
        state.photosLoaded = true
        state.photos = action.payload
      })
      .addCase(fetchPortalPhotos.rejected, (state) => {
        state.photosFetching = false
        state.photosLoaded = true
        state.photosError = true
        state.photos = []
      })
      .addCase(uploadPhotoThunk.fulfilled, (state, action) => {
        state.photos = [action.payload, ...state.photos]
      })

      .addCase(fetchAllProjectsInvoices.pending, (state) => {
        state.crossProjectInvoicesFetching = true
      })
      .addCase(fetchAllProjectsInvoices.fulfilled, (state, action) => {
        state.crossProjectInvoicesFetching = false
        state.crossProjectInvoicesLoaded = true
        state.crossProjectInvoices = action.payload
      })
      .addCase(fetchAllProjectsInvoices.rejected, (state) => {
        state.crossProjectInvoicesFetching = false
        state.crossProjectInvoicesLoaded = true
      })

      .addCase(fetchAllProjectsDocuments.pending, (state) => {
        state.crossProjectDocumentsFetching = true
      })
      .addCase(fetchAllProjectsDocuments.fulfilled, (state, action) => {
        state.crossProjectDocumentsFetching = false
        state.crossProjectDocumentsLoaded = true
        state.crossProjectDocuments = action.payload
      })
      .addCase(fetchAllProjectsDocuments.rejected, (state) => {
        state.crossProjectDocumentsFetching = false
        state.crossProjectDocumentsLoaded = true
      })

      .addCase(fetchAllProjectsPhotos.pending, (state) => {
        state.crossProjectPhotosFetching = true
      })
      .addCase(fetchAllProjectsPhotos.fulfilled, (state, action) => {
        state.crossProjectPhotosFetching = false
        state.crossProjectPhotosLoaded = true
        state.crossProjectPhotos = action.payload
      })
      .addCase(fetchAllProjectsPhotos.rejected, (state) => {
        state.crossProjectPhotosFetching = false
        state.crossProjectPhotosLoaded = true
      })

      .addCase(fetchPortalProfile.pending, (state) => {
        state.profileFetching = true
        state.profileError = false
      })
      .addCase(fetchPortalProfile.fulfilled, (state, action) => {
        state.profileFetching = false
        state.profile = action.payload
      })
      .addCase(fetchPortalProfile.rejected, (state) => {
        state.profileFetching = false
        state.profileError = true
      })
      .addCase(updateProfileThunk.pending, (state) => {
        state.profileSaving = true
        state.profileConflict = false
      })
      .addCase(updateProfileThunk.fulfilled, (state, action) => {
        state.profileSaving = false
        state.profile = action.payload
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.profileSaving = false
        state.profileConflict = !!action.payload?.conflict
      })

      .addCase(fetchPortalCalendarEvents.pending, (state) => {
        state.calendarEventsFetching = true
        state.calendarEventsError = false
      })
      .addCase(fetchPortalCalendarEvents.fulfilled, (state, action) => {
        state.calendarEventsFetching = false
        state.calendarEventsLoaded = true
        state.calendarEvents = action.payload
      })
      .addCase(fetchPortalCalendarEvents.rejected, (state) => {
        state.calendarEventsFetching = false
        state.calendarEventsLoaded = true
        state.calendarEventsError = true
      })
      // Both actions return the one event they touched — replace it in
      // place so every open view (Home, the calendar grid, an open detail
      // panel) reflects the new RSVP/request without a full refetch.
      .addCase(rsvpToCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })
      .addCase(requestCalendarRescheduleThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })
      .addCase(createCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents = [...state.calendarEvents, action.payload]
      })
      .addCase(updateCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })
      .addCase(cancelCalendarEventThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })
      .addCase(acceptProposedTimeThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })
      .addCase(updateReminderPreferenceThunk.fulfilled, (state, action) => {
        state.calendarEvents = state.calendarEvents.map((event) =>
          event.id === action.payload.id ? action.payload : event
        )
      })

      .addCase(logout.fulfilled, () => initialState)
  }
})

export const { selectPortalProject } = portalSlice.actions

export default portalSlice
